'use client'
import { useState, useEffect } from 'react'
import type { ChangeEvent, JSX } from 'react'
import type { UseFormRegister as TypeRegister, FieldError } from 'react-hook-form'
import styled from 'styled-components'
import { noRegister, type TypeThemeColors } from './Input'
import { mq, type Col } from '@/styles'
import { MODALS } from '@/context'
import { useModal } from '@/hooks'


type onChangeType = (event: ChangeEvent<HTMLInputElement>) => void

interface iCheckbox {
  readonly name: string
  readonly label: string
  defaultChecked?: boolean
  readonly required?: boolean
  readonly disabled?: boolean
  readonly readOnly?: boolean
  readonly onChange?: onChangeType
  readonly col?: Col
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly register?: TypeRegister<any>
  error?: FieldError | unknown
  readonly textColor?: TypeThemeColors
  readonly bgColor?: TypeThemeColors
  readonly switchAppearance?: boolean
  modalId?: MODALS
  readonly customTickBox?: boolean
  id?: string
}

interface iLabelProps {
  $col: Col
  $textColor?: TypeThemeColors
  $bgColor: TypeThemeColors
  $readOnly?: boolean
  $disabled?: boolean
  $checked: boolean
}

export const Checkbox = ({ name, label, defaultChecked = undefined, required = false, disabled = false, readOnly = null, onChange = null, col = 12, register = noRegister, textColor = 'black', bgColor = 'white', switchAppearance = false, error, modalId, id='' }: iCheckbox): JSX.Element => {
  const requireProps = typeof required === 'boolean' ? { required } : required
  const { onChange:registerOnChange, name:registerName, ref:registerRef } = register(name, { ...requireProps })
  const [checked, setChecked] = useState(Boolean(defaultChecked))
  const { isActive, payload } = useModal(modalId)

  const handleChange = e => {
    registerOnChange?.(e)
    onChange?.(e)
    setChecked(e.target.checked)
  }

  useEffect(() => {
    if(typeof defaultChecked === 'boolean') setChecked(defaultChecked)
  }, [defaultChecked])

  useEffect(() => {
    // This useEffect will only run if the <Checkbox /> is inside a modal
    if (!modalId || !isActive) return
    setChecked(Boolean(payload?.[name]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, modalId])

  return (
    <Label $col={col} $bgColor={bgColor} $textColor={textColor} $checked={checked} $readOnly={readOnly} $disabled={disabled} data-disabled={disabled}>
      <input type='checkbox' id={id} {...registerRef && { ref: registerRef }} name={registerName ?? name} required={required} readOnly={readOnly} disabled={disabled} onChange={handleChange} {...typeof defaultChecked === 'boolean' && { defaultChecked }}
      />
      {switchAppearance ? <Switch $checked={checked} /> :<TickBox $checked={checked} $error={!!error} />}
      <span>{label}</span>
    </Label>
  )
}

const Label = styled.label<iLabelProps>`
  align-items: flex-start;
  color: var(--color-black);
  cursor: pointer;
  display: flex;
  justify-content: flex-start;
  margin-bottom: 20px;
  opacity: ${({ $disabled, $readOnly }) => $readOnly ? .7 : $disabled ? .3 : 1};
  padding: 4.25px 4.25px 0;
  pointer-events: ${({ $disabled, $readOnly }) => ($readOnly || $disabled) ? 'none' : 'auto'};
  position: relative;
  transition: opacity 250ms linear;
  width: auto;

  ${mq.greaterThan('tablet') } {
    margin-bottom: 30px;
    padding: 12.5px 12.5px 0;
    width: ${({ $col }) => typeof $col === 'number' ? `${100 * $col / 12}%` : $col};
  }

  > div {
    border-color: var(--color-text);
  }

  input {
    opacity: 0;
    pointer-events: ${({ $disabled, $readOnly }) => ($disabled || $readOnly) ? 'none' : 'auto'};
    position: absolute;
    user-select: none;
    visibility: hidden;
  }

  &:hover {
    input + div {
      opacity: .8;
      background: #4d4d4d33;
    }
  }

  span {
    display: inline-block;
    font-size: 15px;
    font-weight: 300;
    margin-top: 3px;
    user-select: none;
    
    &:hover {
    input + div {
      opacity: .8;
      background: #4d4d4d33;
    }
  }
  }
`

const TickBox = styled.div<{ $checked: boolean, $error?: boolean }>`
  background-color: ${({ $checked }) => $checked ? '#4D4D4D' : 'transparent'};
  border: 1px solid;
  border-radius: 8px;
  flex-shrink: 0;
  height: 16px;
  margin-right:10px;
  margin-top: 5px;
  position: relative;
  transition: 250ms background-color var(--transition-ease), 250ms opacity var(--transition-ease);
  user-select: none;
  width: 16px;

  &:hover {
    background: #4d4d4d33;
  }
`

const Switch = styled.div<{ $checked: boolean, $error?: boolean }>`
  align-items: center;
  background: ${({ $checked }) => $checked ? 'var(--color-text)' : 'var(--colo-black)'};
  border-radius: 9.5px;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 17px;
  margin-right:20px;
  margin-top: 5px;
  padding: 0 1px;
  position: relative;
  transition: 250ms background-color linear, 250ms opacity linear;
  width: 30px;

  &:after {
    background: var(--color-white);
    border-radius: 50%;
    content: '';
    height: 15px;
    margin-left: ${({ $checked }) => $checked ? '15px' : 0};
    transition: 250ms margin-left linear;
    width: 15px;
  }

 
`

